import { useCallback, useState } from "react";
import { OLITASK_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE_URL = `${OLITASK_SERVICE_BASE_URL}/api/v1/cancellation-policy-rules`;

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const unwrapResponse = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;

  return data;
};

export default function useCancellationPolicyRules() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAllRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(BASE_URL);
      const normalized = unwrapResponse(response);
      return Array.isArray(normalized) ? normalized : normalized ? [normalized] : [];
    } catch (err) {
      const message = getErrorMessage(err, "Failed to fetch cancellation policy rules");
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const getRuleById = useCallback(async (ruleId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${BASE_URL}/${ruleId}`);
      return unwrapResponse(response);
    } catch (err) {
      const message = getErrorMessage(err, "Failed to fetch cancellation policy rule");
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const getActiveRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${BASE_URL}/active`);
      const data = unwrapResponse(response);
      return Array.isArray(data) ? data : data ? [data] : [];
    } catch (err) {
      const message = getErrorMessage(err, "Failed to fetch active cancellation rules");
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const getActiveRuleByStage = useCallback(async (stage, canceler) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${BASE_URL}/active/stage`, {
        params: { stage, canceler },
      });
      return unwrapResponse(response);
    } catch (err) {
      const message = getErrorMessage(
        err,
        "Failed to fetch active cancellation rule by stage",
      );
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createRule = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(BASE_URL, payload);
      return unwrapResponse(response);
    } catch (err) {
      const message = getErrorMessage(err, "Failed to create cancellation policy rule");
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateRule = useCallback(async (ruleId, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.put(`${BASE_URL}/${ruleId}`, payload);
      return unwrapResponse(response);
    } catch (err) {
      const message = getErrorMessage(err, "Failed to update cancellation policy rule");
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteRule = useCallback(async (ruleId) => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.delete(`${BASE_URL}/${ruleId}`);
      return true;
    } catch (err) {
      const message = getErrorMessage(err, "Failed to delete cancellation policy rule");
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getAllRules,
    getRuleById,
    getActiveRules,
    getActiveRuleByStage,
    createRule,
    updateRule,
    deleteRule,
  };
}
